import React, { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { toast } from "react-hot-toast";
import { Document, Page, pdfjs } from "react-pdf";

import FormField from "@mhj/shared/FormField";
import { apiClient, apiRequest } from "@mhj/shared/api";
import { CATEGORY_LABELS } from "../lib/scholarship";
import type { ScholarshipApplication, ScholarshipCategory, ScholarshipDocument, ScholarshipStatus } from "@mhj/shared/types";

type ApplicationDetail = ScholarshipApplication & { userEmail?: string; userName?: string };

type FilterForm = {
  category: ScholarshipCategory | "";
  status: ScholarshipStatus | "";
  search: string;
  internalNotes: string;
};

pdfjs.GlobalWorkerOptions.workerSrc =
  `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const isPdfDocument = (url: string) => /\.pdf($|\?)/i.test(url);

export default function AdminScholarships() {
  const queryClient = useQueryClient();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selected, setSelected] = useState<ApplicationDetail | null>(null);
  const [jambEpinInput, setJambEpinInput] = useState("");
  const [activeDocument, setActiveDocument] = useState<ScholarshipDocument | null>(null);
  const [pdfNumPages, setPdfNumPages] = useState(0);
  const [pdfPageNumber, setPdfPageNumber] = useState(1);
  const [pdfBlobUrl, setPdfBlobUrl] = useState<string | null>(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const { register, setValue, handleSubmit, watch } = useForm<FilterForm>({
    defaultValues: {
      category: "",
      status: "",
      search: "",
      internalNotes: "",
    },
  });
  const [category, status, search, internalNotes] = watch([
    "category",
    "status",
    "search",
    "internalNotes",
  ]);
  const { data, error: listError } = useQuery({
    queryKey: ["admin", "scholarships", category, status, search],
    queryFn: () =>
      apiRequest<{ applications: ApplicationDetail[] }>("/admin/scholarships", {
        params: {
          category: category || undefined,
          status: status || undefined,
          search: search.trim() || undefined,
        },
      }),
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 10, // 10 minutes
  });

  const { data: detailData, error: detailError } = useQuery({
    queryKey: ["admin", "scholarship", selectedId],
    enabled: !!selectedId,
    queryFn: () =>
      apiRequest<{ application: ApplicationDetail }>(
        `/admin/scholarships/${selectedId}`
      ),
  });

  useEffect(() => {
    if (detailData?.application) {
      setSelected(detailData.application);
      setValue("internalNotes", detailData.application.internalNotes ?? "");
      setJambEpinInput(detailData.application.jambEPin ?? "");
    }
  }, [detailData, setValue]);

  useEffect(() => {
    if (listError) {
      toast.error((listError as Error).message);
    }
  }, [listError]);

  useEffect(() => {
    if (detailError) {
      toast.error((detailError as Error).message);
    }
  }, [detailError]);

  useEffect(() => {
    if (activeDocument && isPdfDocument(activeDocument.url)) {
      setPdfPageNumber(1);
      setPdfNumPages(0);
    }
  }, [activeDocument]);

  useEffect(() => {
    if (!activeDocument || !isPdfDocument(activeDocument.url)) {
      setPdfLoading(false);
      setPdfBlobUrl((current) => {
        if (current) URL.revokeObjectURL(current);
        return null;
      });
      return;
    }

    let cancelled = false;
    setPdfLoading(true);

    void apiClient
      .get<Blob>(`/admin/scholarships/documents/${activeDocument.id}/raw`, {
        responseType: "blob",
      })
      .then((response) => {
        if (cancelled) return;
        const url = URL.createObjectURL(response.data);
        setPdfBlobUrl((current) => {
          if (current) URL.revokeObjectURL(current);
          return url;
        });
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        toast.error(
          (error as { message?: string })?.message ?? "Unable to load PDF file.",
        );
      })
      .finally(() => {
        if (!cancelled) setPdfLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [activeDocument]);

  const statusMutation = useMutation({
    mutationFn: (nextStatus: ScholarshipStatus) =>
      apiRequest<{ application: ApplicationDetail }>(
        `/admin/scholarships/${selected?.id}/status`,
        {
          method: "POST",
          data: { status: nextStatus, internalNotes: internalNotes || null },
          withCsrf: true,
        }
      ),
    onSuccess: async (response) => {
      setSelected(response.application);
      await queryClient.invalidateQueries({ queryKey: ["admin", "scholarships"] });
      toast.success("Application status updated.");
    },
  });

  const verifyMutation = useMutation({
    mutationFn: (doc: ScholarshipDocument) =>
      apiRequest<{ application: ApplicationDetail }>(
        `/admin/scholarships/${selected?.id}/verify-doc`,
        {
          method: "POST",
          data: { documentId: doc.id, verified: !doc.verified },
          withCsrf: true,
        }
      ),
    onSuccess: async (response) => {
      setSelected(response.application);
      setActiveDocument(null);
      await queryClient.invalidateQueries({ queryKey: ["admin", "scholarships"] });
      toast.success("Document verification updated.");
    },
  });

  const jambEpinMutation = useMutation({
    mutationFn: (jambEPin: string) =>
      apiRequest<{ application: ApplicationDetail }>(
        `/admin/scholarships/${selected?.id}/jamb-epin`,
        {
          method: "POST",
          data: { jambEPin },
          withCsrf: true,
        },
      ),
    onSuccess: async (response) => {
      setSelected(response.application);
      setJambEpinInput(response.application.jambEPin ?? "");
      await queryClient.invalidateQueries({ queryKey: ["admin", "scholarships"] });
      toast.success("JAMB e-pin updated.");
    },
  });

  const applications = data?.applications ?? [];

  const handleStatus = async (nextStatus: ScholarshipStatus) => {
    if (!selected) return;
    try {
      await statusMutation.mutateAsync(nextStatus);
    } catch (err) {
      toast.error((err as Error).message);
    }
  };

  const handleVerifyDoc = async (doc: ScholarshipDocument) => {
    if (!selected) return;
    try {
      await verifyMutation.mutateAsync(doc);
      setActiveDocument((current) =>
        current && current.id === doc.id
          ? { ...current, verified: !current.verified }
          : current,
      );
    } catch (err) {
      toast.error((err as Error).message);
    }
  };

  const handleSaveJambEpin = async () => {
    if (!selected || selected.category !== "jamb") return;
    const epin = jambEpinInput.trim();
    if (epin.length < 4) {
      toast.error("Enter a valid JAMB e-pin.");
      return;
    }
    try {
      await jambEpinMutation.mutateAsync(epin);
    } catch (err) {
      toast.error((err as Error).message);
    }
  };

  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_1.2fr]">
      <div className="glass-card rounded-3xl p-6">
        <p className="text-xs uppercase tracking-[0.3em] text-blue-400">
          Applications
        </p>
        <div className="mt-4 grid gap-4 md:grid-cols-2">
          <div className="md:col-span-2">
            <FormField label="Candidate search">
              <input
                type="text"
                {...register("search")}
                placeholder="Name, email, or phone"
                className="rounded-2xl border border-black/10 bg-white/60 px-4 py-3 text-black focus:border-blue-400 focus:outline-none"
              />
            </FormField>
          </div>
          <FormField label="Category">
            <select
              {...register("category")}
              className="rounded-2xl border border-black/10 bg-white/60 px-4 py-3 text-black focus:border-blue-400 focus:outline-none"
            >
              <option value="">All</option>
              <option value="jamb">JAMB</option>
              <option value="undergraduate">Undergraduate</option>
              <option value="postgraduate">Postgraduate</option>
              <option value="others">Others</option>
            </select>
          </FormField>
          <FormField label="Status">
            <select
              {...register("status")}
              className="rounded-2xl border border-black/10 bg-white/60 px-4 py-3 text-black focus:border-blue-400 focus:outline-none"
            >
              <option value="">All</option>
              <option value="submitted">Submitted</option>
              <option value="under_review">Under review</option>
              <option value="approved">Approved</option>
              <option value="disbursed">Disbursed</option>
              <option value="rejected">Rejected</option>
            </select>
          </FormField>
        </div>
        <button
          onClick={handleSubmit(() =>
            queryClient.invalidateQueries({ queryKey: ["admin", "scholarships"] })
          )}
          className="mt-4 rounded-full border border-black/20 px-5 py-2 text-xs uppercase tracking-[0.2em] text-black/70 transition hover:border-blue-400 hover:text-blue-400"
        >
          Apply filters
        </button>
        <div className="mt-6 space-y-3 text-sm text-white/70">
          {applications.length === 0 ? (
            <p>No applications found.</p>
          ) : (
            applications.map((item) => (
              <button
                key={item.id}
                onClick={() => setSelectedId(item.id)}
                className="w-full rounded-2xl border border-black/10 bg-white/60 px-4 py-3 text-left transition hover:border-blue-400"
              >
                <p className="font-semibold text-black">
                  {CATEGORY_LABELS[item.category]} — {item.userName ?? "Applicant"}
                </p>
                <p className="text-xs text-black/50">
                  {item.userEmail ?? "No email"}
                  {item.userPhone ? ` · ${item.userPhone}` : ""}
                  {` · ${item.status}`}
                </p>
              </button>
            ))
          )}
        </div>
      </div>
      <div className="glass-card rounded-3xl p-6">
        <p className="text-xs uppercase tracking-[0.3em] text-blue-400">
          Application detail
        </p>
        {selected ? (
          <>
            <h3 className="mt-3 text-xl font-semibold text-black">
              {CATEGORY_LABELS[selected.category]} application
            </h3>
            <p className="mt-2 text-sm text-black/60">
              {selected.userName}
              {selected.userEmail ? ` · ${selected.userEmail}` : ""}
              {selected.userPhone ? ` · ${selected.userPhone}` : ""}
            </p>
            <div className="mt-4 grid gap-3 text-xs text-black/60 md:grid-cols-2">
              <div>LGA: {selected.lga}</div>
              <div>State: {selected.state}</div>
              <div>Status: <span className="font-semibold uppercase text-shadow-cyan-900">{selected.status}</span></div>
              <div>Submitted: {selected.submittedAt ? "Yes" : "No"}</div>
            </div>
            {selected.category === "jamb" ? (
              <div className="mt-6 rounded-2xl border border-black/10 bg-white/60 p-4">
                <p className="text-xs uppercase tracking-[0.2em] text-blue-400">
                  JAMB details
                </p>
                <div className="mt-3 grid gap-3 text-xs text-black/60 md:grid-cols-2">
                  <p>
                    Profile code:
                    {" "}
                    <span className="font-semibold text-black">
                      {selected.profileCode ?? "N/A"}
                    </span>
                  </p>
                  <p>
                    Current e-pin:
                    {" "}
                    <span className="font-semibold text-black">
                      {selected.jambEPin ?? "Not set"}
                    </span>
                  </p>
                </div>
                <div className="mt-4 flex flex-wrap items-end gap-3">
                  <FormField label="Set candidate JAMB e-pin">
                    <input
                      type="text"
                      value={jambEpinInput}
                      onChange={(event) => setJambEpinInput(event.target.value)}
                      placeholder="Enter e-pin"
                      className="rounded-2xl border border-black/10 bg-white/60 px-4 py-3 text-black focus:border-blue-400 focus:outline-none"
                    />
                  </FormField>
                  <button
                    type="button"
                    onClick={() => void handleSaveJambEpin()}
                    disabled={
                      jambEpinMutation.isPending ||
                      !["approved", "disbursed"].includes(selected.status) ||
                      !selected.profileCode?.trim()
                    }
                    className="rounded-full bg-blue-500 px-5 py-2 text-xs uppercase tracking-[0.2em] text-white transition hover:bg-blue-400 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {jambEpinMutation.isPending ? "Saving..." : "Save e-pin"}
                  </button>
                </div>
                {!["approved", "disbursed"].includes(selected.status) ||
                !selected.profileCode?.trim() ? (
                  <p className="mt-2 text-[11px] text-amber-700">
                    E-pin can only be set after approval/acceptance and when profile code is available.
                  </p>
                ) : null}
              </div>
            ) : null}
            <div className="mt-6 space-y-3">
              <p className="text-xs uppercase tracking-[0.2em] text-blue-400">
                Documents
              </p>
              {selected.documents.length === 0 ? (
                <p className="text-xs text-white/50">No uploads yet.</p>
              ) : (
                selected.documents.map((doc) => (
                  <div
                    key={doc.id}
                    className="flex items-center justify-between rounded-2xl border border-black/10 bg-white/60 px-4 py-3 text-xs"
                  >
                    <div>
                      <p className="uppercase tracking-[0.2em] text-black/70">
                        {doc.docType.replace(/_/g, " ")}
                      </p>
                      <button
                        type="button"
                        onClick={() => setActiveDocument(doc)}
                        className="text-blue-500 hover:text-blue-700"
                      >
                        View document
                      </button>
                    </div>
                    <span
                      className={`rounded-full px-3 py-1 text-[10px] uppercase tracking-[0.2em] ${
                        doc.verified
                          ? "bg-emerald-100 text-emerald-700"
                          : "bg-amber-100 text-amber-700"
                      }`}
                    >
                      {doc.verified ? "Verified" : "Pending"}
                    </span>
                  </div>
                ))
              )}
            </div>
            <div className="mt-6">
              <FormField label="Internal notes">
                <textarea
                  {...register("internalNotes")}
                  rows={4}
                  className="rounded-2xl border border-black/10 bg-white/60 px-4 py-3 text-black focus:border-blue-400 focus:outline-none"
                />
              </FormField>
            </div>
            <div className="mt-6 flex flex-wrap gap-3">
              <button
                onClick={() => void handleStatus("under_review")}
                disabled={statusMutation.isPending}
                className="rounded-full border border-black/20 px-5 py-2 text-xs uppercase tracking-[0.2em] text-black/70 transition hover:border-blue-400 hover:text-blue-200 disabled:cursor-not-allowed disabled:opacity-60"
              >
                Mark under review
              </button>
              <button
                onClick={() => void handleStatus("approved")}
                disabled={statusMutation.isPending}
                className="rounded-full bg-emerald-500/80 px-5 py-2 text-xs uppercase tracking-[0.2em] text-white transition hover:bg-emerald-400 disabled:cursor-not-allowed disabled:opacity-60"
              >
                Approve
              </button>
              <button
                onClick={() => void handleStatus("disbursed")}
                disabled={statusMutation.isPending}
                className="rounded-full bg-blue-600 px-5 py-2 text-xs uppercase tracking-[0.2em] text-white transition hover:bg-blue-500 disabled:cursor-not-allowed disabled:opacity-60"
              >
                Mark disbursed
              </button>
              <button
                onClick={() => void handleStatus("rejected")}
                disabled={statusMutation.isPending}
                className="rounded-full bg-red-500/80 px-5 py-2 text-xs uppercase tracking-[0.2em] text-white transition hover:bg-red-400 disabled:cursor-not-allowed disabled:opacity-60"
              >
                Reject
              </button>
            </div>
          </>
        ) : (
          <p className="mt-4 text-sm text-white/60">
            Select an application to review.
          </p>
        )}
      </div>
      {activeDocument ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="w-full max-w-4xl rounded-3xl border border-black/10 bg-white p-5 shadow-xl">
            <div className="mb-4 flex items-center justify-between">
              <p className="text-xs uppercase tracking-[0.25em] text-black/60">
                {activeDocument.docType.replace(/_/g, " ")}
              </p>
              <button
                type="button"
                onClick={() => setActiveDocument(null)}
                className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-black/15 text-black/70 transition hover:border-black/30 hover:text-black"
                aria-label="Close document viewer"
              >
                <svg
                  className="h-4 w-4"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  aria-hidden="true"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 6l12 12M18 6L6 18" />
                </svg>
              </button>
            </div>

            <div className="h-[60vh] overflow-hidden rounded-2xl border border-black/10 bg-slate-50">
              {isPdfDocument(activeDocument.url) ? (
                <div className="flex h-full w-full items-center justify-center overflow-auto">
                  {pdfLoading ? (
                    <p className="text-sm text-black/60">Loading PDF...</p>
                  ) : pdfBlobUrl ? (
                    <Document
                      file={pdfBlobUrl}
                      loading={<p className="text-sm text-black/60">Loading PDF...</p>}
                      onLoadSuccess={({ numPages }) => {
                        setPdfNumPages(numPages);
                      }}
                      onLoadError={() => {
                        toast.error("Unable to preview this PDF.");
                      }}
                    >
                      <Page
                        pageNumber={pdfPageNumber}
                        renderTextLayer={false}
                        renderAnnotationLayer={false}
                      />
                    </Document>
                  ) : (
                    <p className="text-sm text-black/60">Failed to load PDF file.</p>
                  )}
                </div>
              ) : (
                <img
                  src={activeDocument.url}
                  alt="Scholarship document preview"
                  className="h-full w-full object-contain"
                />
              )}
            </div>

            <div className="mt-4 flex items-center justify-end gap-3">
              {isPdfDocument(activeDocument.url) ? (
                <div className="mr-auto flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setPdfPageNumber((page) => Math.max(1, page - 1))}
                    disabled={pdfPageNumber <= 1}
                    className="rounded-full border border-black/20 px-3 py-1 text-[11px] uppercase tracking-[0.2em] text-black/70 transition hover:border-black/40 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Prev
                  </button>
                  <span className="text-xs text-black/60">
                    Page {pdfPageNumber}
                    {pdfNumPages ? ` / ${pdfNumPages}` : ""}
                  </span>
                  <button
                    type="button"
                    onClick={() =>
                      setPdfPageNumber((page) =>
                        pdfNumPages ? Math.min(pdfNumPages, page + 1) : page + 1,
                      )
                    }
                    disabled={!!pdfNumPages && pdfPageNumber >= pdfNumPages}
                    className="rounded-full border border-black/20 px-3 py-1 text-[11px] uppercase tracking-[0.2em] text-black/70 transition hover:border-black/40 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
              ) : null}
              <button
                type="button"
                onClick={() => setActiveDocument(null)}
                className="rounded-full border border-black/20 px-5 py-2 text-xs uppercase tracking-[0.2em] text-black/70 transition hover:border-black/40"
              >
                Close
              </button>
              <button
                type="button"
                onClick={() => void handleVerifyDoc(activeDocument)}
                disabled={verifyMutation.isPending}
                className="rounded-full bg-blue-500 px-5 py-2 text-xs uppercase tracking-[0.2em] text-white transition hover:bg-blue-400 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {activeDocument.verified ? "Unverify" : "Verify"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
